import { useState } from "react";
import { Button } from "../components/ui/Button";
import { CellInput, Hint, Input } from "../components/ui/Field";
import { Notice } from "../components/ui/Notice";
import { Panel, SectionTitle, Toolbar } from "../components/ui/Panel";
import { Tag } from "../components/ui/Tag";
import { Thumb } from "../components/ui/Thumb";
import { DataTable, type Column } from "../components/data/DataTable";
import { I18nField } from "../components/data/I18nField";
import { RewardDialog } from "../components/data/RewardDialog";
import { ApiTag } from "../components/apilens/ApiLens";
import { useAssetUpload } from "../components/data/useAssetUpload";
import { fmt } from "../lib/model";
import { REWARD_LABEL, type Reward } from "../lib/reward";
import { slotIssue, treeIssue, TREE_DEFAULTS, type PettownRewardKind, type PettownSlot } from "../lib/pettown";
import { useT } from "../lib/i18n";

/**
 * 副建筑槽位：一行一块地。
 *
 * 槽位号 `r<环号>_<两位环内位号>` 是**身份**——玩家的解锁进度存的就是它，发布后不可改。
 * 位置由环号 + 环内位号决定（每一环从右下方位起、顺时针）；解锁顺序是「解锁星数」
 * 单独一列，**与环号无关**：内圈开到一半先放一个外圈地标是合法配法。
 *
 * 右侧是布局示意，画的就是环号 / 位号推出来的位置——配错位置在表格里看不出来，
 * 在图上一眼就看出来（两栋楼叠在一起）。
 */
export function PettownSlotPage({
  slots, maxStar, mainSkin, editable, itemCards, idolCards, onPatch, onAdd, onRemove,
}: {
  slots: PettownSlot[];
  /** 累计星数上限，用来判断解锁星数是不是够得着 */
  maxStar: number;
  /** 主建筑第 1 星的外观图，只画在布局示意中间——让运营一眼看出主副两边配的是不是同一套美术 */
  mainSkin?: string;
  editable: boolean;
  itemCards: string[];
  idolCards: string[];
  onPatch: (i: number, patch: Partial<PettownSlot>) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState<number | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const up = useAssetUpload<number>({ module: "pettown" });

  // 行的身份是下标，不是 slot：slot 是个可编辑文本框，拿它当 rowKey 会让每敲一个
  // 字符整行重挂、输入框当场失焦（同社交渠道页的 code 列）
  const idxOf = (s: PettownSlot) => slots.indexOf(s);

  const asReward = (s: PettownSlot): Reward =>
    ({ id: s.slot, levelId: 0, name: "", i18n: {}, items: s.reward ?? [] });
  const rewardText = (s: PettownSlot) =>
    (s.reward ?? [])
      .map((it) => `${t(REWARD_LABEL[it.kind])}${it.ref ? ` · ${it.ref}` : ""} × ${it.qty == null ? t("common.pending") : fmt(it.qty)}`)
      .join("，");

  const columns: Column<PettownSlot>[] = [
    {
      key: "slot", api: "ptSlot.slot", width: 120, tint: "editable", stickyLeft: 0,
      title: <span className="inline-flex items-center">{t("pt.col.slot")}<Hint text={t("pt.col.slot.hint")} /></span>,
      render: (s) => {
        const iss = slotIssue(s, slots, maxStar);
        return (
          <span className="flex flex-col items-stretch gap-0.5">
            <Input value={s.slot} size="sm" disabled={!editable} placeholder="r1_01"
              className={iss === "pt.err.slotId" || iss === "pt.err.slotDup" ? "border-danger" : ""}
              onChange={(v) => onPatch(idxOf(s), { slot: v.trim() })} />
            {iss && <span className="text-[11px] leading-tight text-danger">{t(iss)}</span>}
          </span>
        );
      },
    },
    {
      key: "img", api: "ptSlot.img", title: t("pt.col.img"), width: 150, align: "center",
      render: (s) => (
        <span className="flex items-center justify-center gap-1.5">
          {s.imageUrl
            ? <Thumb src={s.imageUrl} title={s.slot} className="h-9 w-9 rounded-sm border border-line object-cover" />
            : <span className="text-danger text-[11px]">{t("pt.err.slotImg")}</span>}
          <Button size="sm" disabled={!editable || up.busy}
            onClick={() => up.pick(idxOf(s), (url, i) => onPatch(i, { imageUrl: url }))}>
            {s.imageUrl ? t("card.reupload") : t("card.upload")}
          </Button>
        </span>
      ),
    },
    {
      key: "name", api: "ptSlot.name", title: t("pt.col.name"), width: 200,
      render: (s) => (
        <I18nField value={s.nameI18n} disabled={!editable} width={200} label={s.slot || undefined}
          onChange={(v) => onPatch(idxOf(s), { nameI18n: v })} />
      ),
    },
    {
      key: "pos", api: "ptSlot.pos", width: 150, tint: "editable",
      title: <span className="inline-flex items-center">{t("pt.col.pos")}<Hint text={t("pt.col.pos.hint")} /></span>,
      render: (s) => (
        <span className="flex items-center gap-1">
          <CellInput countOnly value={s.ring} disabled={!editable} onChange={(v) => onPatch(idxOf(s), { ring: v ?? 1 })} />
          <span className="text-ink-placeholder">·</span>
          <CellInput countOnly value={s.idx} disabled={!editable} onChange={(v) => onPatch(idxOf(s), { idx: v ?? 1 })} />
        </span>
      ),
    },
    {
      key: "unlockStar", api: "ptSlot.unlockStar", width: 140, align: "right", tint: "editable",
      title: <span className="inline-flex items-center">{t("pt.col.unlockStar")}<Hint text={t("pt.col.unlockStar.hint")} /></span>,
      // 只显示累计星数本身：星是唯一单位（leo 2026-09-17），不再在下面标「几级几星」
      render: (s) => (
        <CellInput countOnly value={s.unlockStar} disabled={!editable}
          onChange={(v) => onPatch(idxOf(s), { unlockStar: v ?? 1 })} />
      ),
    },
    {
      // 类型 = 体力树的那一栋是萌宠小镇体力的唯一来源（不自动恢复骰子）；三个基础属性，天赋树的体力树三条线在上面加
      key: "kind", api: "ptSlot.kind", title: t("ptx.col.kind"), width: 300,
      render: (s) => {
        const issue = treeIssue(s);
        return (
          <span className="flex flex-col gap-1">
            <select value={s.kind ?? "normal"} disabled={!editable} className="h-7 w-24 rounded-base border border-line bg-white px-1.5 text-xs"
              onChange={(e) => onPatch(idxOf(s), e.target.value === "stamina_tree"
                // 体力树一开始就在：解锁星数跟着改成 0
                ? { kind: "stamina_tree", unlockStar: 0, treeGrowSec: s.treeGrowSec ?? TREE_DEFAULTS.treeGrowSec, treeCap: s.treeCap ?? TREE_DEFAULTS.treeCap, treeCount: s.treeCount ?? TREE_DEFAULTS.treeCount }
                // 切回普通把属性清掉：服务端不收普通副建筑带体力树属性；解锁星数回到 ≥ 1
                : { kind: "normal", unlockStar: Math.max(1, s.unlockStar), treeGrowSec: null, treeCap: null, treeCount: null })}>
              <option value="normal">{t("ptx.kind.normal")}</option>
              <option value="stamina_tree">{t("ptx.kind.tree")}</option>
            </select>
            {s.kind === "stamina_tree" && (
              <span className="flex flex-wrap items-center gap-1 text-xs text-ink-secondary">
                <span data-api="ptSlot.treeGrowSec" className="w-14"><CellInput countOnly value={s.treeGrowSec == null ? null : Math.round(s.treeGrowSec / 60)} disabled={!editable}
                  onChange={(v) => onPatch(idxOf(s), { treeGrowSec: v == null ? null : v * 60 })} /></span>{t("talentx.unit.minPerPt")}<ApiTag id="ptSlot.treeGrowSec" />
                <span data-api="ptSlot.treeCap" className="w-12"><CellInput countOnly value={s.treeCap} disabled={!editable} onChange={(v) => onPatch(idxOf(s), { treeCap: v })} /></span>{t("talentx.unit.ptPerTree")}<ApiTag id="ptSlot.treeCap" />
                <span data-api="ptSlot.treeCount" className="w-12"><CellInput countOnly value={s.treeCount} disabled={!editable} onChange={(v) => onPatch(idxOf(s), { treeCount: v })} /></span>{t("talentx.unit.trees")}<ApiTag id="ptSlot.treeCount" />
              </span>
            )}
            {s.kind === "stamina_tree" && !issue && s.treeGrowSec && s.treeCap
              ? <span className="text-[11px] text-ink-placeholder">{t("ptx.tree.fillTime", { h: ((s.treeCap * s.treeGrowSec) / 3600).toFixed(1) })}</span> : null}
            {issue && <span className="text-[11px] text-danger">{t(issue)}</span>}
          </span>
        );
      },
    },
    {
      key: "reward", api: "ptSlot.reward", title: t("pt.col.reward"), width: 260,
      render: (s) => (
        <span className="flex items-center gap-2">
          <select
            value={s.rewardKind}
            disabled={!editable}
            className="h-7 rounded-base border border-line bg-white px-1.5 text-xs"
            onChange={(e) => {
              const kind = e.target.value as PettownRewardKind;
              // 切到「不给」时把内容一起清掉：留着的话下次切回 item 会莫名其妙冒出旧奖励
              onPatch(idxOf(s), { rewardKind: kind, reward: kind === "item" ? (s.reward ?? []) : null });
            }}
          >
            <option value="none">{t("pt.reward.none")}</option>
            <option value="item">{t("pt.reward.item")}</option>
            <option value="buff">{t("pt.reward.buff")}</option>
          </select>
          {s.rewardKind === "item" && (
            <>
              <span className="min-w-0 flex-1 truncate" title={rewardText(s)}>
                {s.reward?.length ? rewardText(s) : <span className="text-danger">{t("pt.err.rewardEmpty")}</span>}
              </span>
              <Button size="sm" className="flex-none" onClick={() => setEditing(idxOf(s))}>
                {editable ? t("common.edit") : t("common.view")}
              </Button>
            </>
          )}
          {s.rewardKind === "buff" && <span className="text-[11px] text-warning">{t("pt.reward.buffNote")}</span>}
        </span>
      ),
    },
    {
      key: "op", title: t("cost.col.op"), width: 90, align: "center",
      render: (s) => (
        <Button size="sm" tone="danger" disabled={!editable} onClick={() => onRemove(idxOf(s))}>
          {t("common.delete")}
        </Button>
      ),
    },
  ];

  const cur = editing == null ? undefined : slots[editing];

  return (
    <div>
      <Toolbar
        left={<span className="text-xs text-ink-placeholder">{t("pt.slot.note")}</span>}
        right={editable && <Button size="sm" tone="success" onClick={onAdd}>{t("pt.addSlot")}</Button>}
      />

      {up.error && <div className="mb-3"><Notice tone="danger">{up.error}</Notice></div>}

      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-[680px] flex-1">
          <Panel>
            <SectionTitle extra={<Tag>{t("pt.slot.count", { n: slots.length })}</Tag>}>{t("nav.pettown-slot")}</SectionTitle>
            <Notice tone="info">{t("pt.slot.rule")}</Notice>
            {(() => {
              const trees = slots.filter((s) => s.kind === "stamina_tree");
              if (trees.length === 1) return <div className="mt-2"><Notice tone="info">{t("ptx.tree.one", { slot: trees[0].slot })}</Notice></div>;
              if (trees.length === 0) return <div className="mt-2"><Notice tone="danger">{t("ptx.tree.none")}</Notice></div>;
              return <div className="mt-2"><Notice tone="danger">{t("ptx.tree.many", { slots: trees.map((s) => s.slot).join(t("talentx.listSep")) })}</Notice></div>;
            })()}
            <div className="mt-3">
              <DataTable columns={columns} rows={slots} rowKey={idxOf} fixedLayout minWidth={1380}
                empty={t("pt.slot.empty")}
                onRowClick={(s) => setSel(s.slot)}
                rowTint={(s) => (slotIssue(s, slots, maxStar) ? "bg-danger-50/40" : s.slot === sel ? "bg-primary-50" : undefined)} />
            </div>
            {up.input}
          </Panel>
        </div>
        <div className="w-[300px] flex-none">
          <Panel>
            <SectionTitle>{t("pt.layout")}</SectionTitle>
            <SlotLayout slots={slots} sel={sel} mainSkin={mainSkin} onSel={setSel} />
            <p className="mt-2 text-xs leading-relaxed text-ink-placeholder">{t("pt.layout.note")}</p>
          </Panel>
        </div>
      </div>

      {cur && (
        <RewardDialog
          reward={asReward(cur)}
          editable={editable}
          hideName
          title={t("pt.dlg.reward", { slot: cur.slot || t("common.pending") })}
          itemCards={itemCards}
          idolCards={idolCards}
          onClose={() => setEditing(null)}
          onSave={(r) => onPatch(editing!, { reward: r.items })}
        />
      )}
    </div>
  );
}

/**
 * 布局示意：每一环从右下（45°）起顺时针均分一圈，中间是主建筑。
 *
 * 画的是「环号 + 位号推出来的位置」，不是美术稿——它要回答的只有一个问题：
 * 这几栋楼有没有配到同一个位置上。真实坐标由客户端按美术稿摆。
 *
 * **格子里放的是这一栋真正配的那张图**：只写槽位号的话，运营对着 9 个一模一样的
 * 小方块，看不出「r1_07 配成了咖啡馆的图」这类配错——而那恰恰是这张图最该拦下的错。
 * 没配图的（理论上过不了写接口）退回纯文字块，不画一个破图标。
 */
function SlotLayout({ slots, sel, mainSkin, onSel }: { slots: PettownSlot[]; sel: string | null; mainSkin?: string; onSel: (s: string) => void }) {
  const t = useT();
  const rings = [...new Set(slots.map((s) => s.ring))].sort((a, b) => a - b);
  const cx = 150, cy = 150;
  return (
    <svg viewBox="0 0 300 300" className="w-full rounded-base bg-fill-light">
      <rect x="122" y="122" width="56" height="56" rx="6" className="fill-primary opacity-80" />
      {mainSkin && (
        <>
          <clipPath id="pt-clip-main"><rect x="139" y="127" width="22" height="22" rx="4" /></clipPath>
          <image href={mainSkin} x="139" y="127" width="22" height="22"
            clipPath="url(#pt-clip-main)" preserveAspectRatio="xMidYMid slice" />
        </>
      )}
      <text x={cx} y={mainSkin ? 163 : cy + 4} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="600">{t("building.isMain")}</text>
      {slots.map((s) => {
        // 半径按环号递增；同环的位号均分一圈，起点 45°（右下）
        const ringIdx = Math.max(0, rings.indexOf(s.ring));
        const count = Math.max(1, slots.filter((x) => x.ring === s.ring).length);
        const a = ((45 + (s.idx - 1) * (360 / count)) * Math.PI) / 180;
        const r = 46 * (2.25 + ringIdx * 1.4);
        const x = cx + Math.cos(a) * r - 25;
        const y = cy + Math.sin(a) * r - 24;
        const on = sel === s.slot;
        const clip = `pt-clip-${s.slot}`;
        return (
          <g key={`${s.slot}-${s.ring}-${s.idx}`} onClick={() => onSel(s.slot)} className="cursor-pointer">
            <title>{t("ptx.layout.tip", { slot: s.slot, name: s.nameI18n.en ?? "", n: s.unlockStar })}</title>
            <rect x={x} y={y} width="50" height="48" rx="5"
              className={on ? "fill-primary-lighter stroke-primary" : "fill-white stroke-line"} />
            {s.imageUrl && (
              <>
                <clipPath id={clip}>
                  <rect x={x + 16} y={y + 3} width="18" height="18" rx="3" />
                </clipPath>
                {/* preserveAspectRatio slice：楼图是方的，非方的也宁可裁边不变形 */}
                <image
                  href={s.imageUrl} x={x + 16} y={y + 3} width="18" height="18"
                  clipPath={`url(#${clip})`} preserveAspectRatio="xMidYMid slice"
                />
              </>
            )}
            <text x={x + 25} y={y + (s.imageUrl ? 32 : 20)} textAnchor="middle" fontSize="9" className="fill-ink-regular">
              {s.slot}
            </text>
            <text x={x + 25} y={y + (s.imageUrl ? 42 : 33)} textAnchor="middle" fontSize="9" className="fill-ink-secondary">
              {s.unlockStar} ★
            </text>
          </g>
        );
      })}
    </svg>
  );
}
